import type {
  WidgetConfig,
  ContactFormLabels,
  PreChatFormLabels,
  Message,
  MessageSource,
  WidgetState,
  UserIdentity,
  HistoryMessage,
} from "./types/messages";
import type { ChatWindowElements } from "./components/ChatWindow";
import {
  fetchWidgetConfig,
  mergeConfig,
  preChatLabelsForLanguage,
} from "./services/config";
import { WidgetWebSocket } from "./services/websocket";
import { deriveAccentPalette, readableLinkColor } from "./utils/palette";
import { submitFeedback, removeFeedback } from "./services/feedback";
import { createContactForm } from "./components/ContactForm";
import { createPreChatForm } from "./components/PreChatForm";
import {
  createFloatingButton,
  updateFloatingButton,
  type LauncherOptions,
} from "./components/FloatingButton";
import {
  createChatWindow,
  setChatWindowVisibility,
  addChatMessage,
  updateChatMessage,
  showTypingIndicator,
  hideTypingIndicator,
  setInputEnabled,
} from "./components/ChatWindow";

// Import styles as raw strings
import baseStyles from "./styles/base.css?raw";
import themeStyles from "./styles/themes.css?raw";
import markdownStyles from "./styles/markdown.css?raw";
import presetStyles from "./styles/presets.css?raw";
import katexCssSource from "katex/dist/katex.min.css?raw";

function resolveWidgetAssetBaseUrl(): string {
  if (typeof document !== "undefined") {
    if (
      document.currentScript instanceof HTMLScriptElement &&
      document.currentScript.src
    ) {
      return new URL("./", document.currentScript.src).href;
    }

    const scriptElements = Array.from(
      document.querySelectorAll("script[src]"),
    ) as HTMLScriptElement[];
    const widgetScript = scriptElements.reverse().find((scriptElement) => {
      try {
        const scriptUrl = new URL(scriptElement.src);
        return (
          scriptUrl.pathname.endsWith("/widget.js") ||
          scriptUrl.pathname.endsWith("/widget.iife.js")
        );
      } catch {
        return false;
      }
    });

    if (widgetScript?.src) {
      return new URL("./", widgetScript.src).href;
    }
  }

  return "https://app.lizadocs.com/widget/";
}

const widgetAssetBaseUrl = resolveWidgetAssetBaseUrl();

// sessionStorage flag preventing re-show of the pre-chat form after the
// visitor already submitted or skipped it within the same browser session.
// Persists across widget re-mounts but resets when the tab/session ends — at
// which point the backend's session_id continuity still suppresses re-asking
// for true returning visitors who completed the form in a previous session.
const PRE_CHAT_COMPLETED_STORAGE_KEY = "lizadocs_pre_chat_completed";

// Grace period before an idle closed widget drops its WebSocket. Long enough
// that toggling the window does not churn connections, short enough that a
// page full of closed widgets holds no sockets. Connections are opened
// lazily on first open - embeds on multi-page apps used to cost one socket
// per page view with nobody chatting.
const IDLE_DISCONNECT_MS = 120_000;

function buildKatexStyles(widgetAssetBaseUrl: string): string {
  return katexCssSource.replaceAll(
    "url(fonts/",
    `url(${widgetAssetBaseUrl}katex/fonts/`,
  );
}

/**
 * LizaDocsChat Web Component
 *
 * Usage:
 * <lizadocs-chat widget-id="abc123"></lizadocs-chat>
 *
 * Attributes:
 * - widget-id (required for production): Widget ID from dashboard
 * - theme: "light" | "dark" (overrides API config)
 * - position: "bottom-right" | "bottom-left" (overrides API config)
 * - api-url: Custom API base URL
 * - config: JSON string with full config (for preview mode, skips API fetch)
 * - preview: "true" to disable WebSocket and start open
 */
export class LizaDocsChat extends HTMLElement {
  // Shadow DOM root
  private shadow: ShadowRoot;

  // State
  private state: WidgetState = {
    isOpen: false,
    isConnected: false,
    isLoading: false,
    messages: [],
  };

  // Config
  private config: WidgetConfig | null = null;
  private widgetId: string | null = null;
  private apiUrl: string = "https://api.lizadocs.com/api";
  private isPreviewMode: boolean = false;
  private previewStartsOpen: boolean = true;

  // Services
  private websocket: WidgetWebSocket | null = null;
  private idleDisconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // UI Elements
  private toggleButton: HTMLButtonElement | null = null;
  private chatWindow: ChatWindowElements | null = null;
  private container: HTMLDivElement | null = null;
  private contactFormElement: HTMLElement | null = null;
  private contactFormLabels: ContactFormLabels | undefined;
  private preChatFormElement: HTMLElement | null = null;
  private preChatLabels: PreChatFormLabels | null = null;

  // Pre-chat lifecycle in this widget instance — drives whether the upfront
  // contact form is shown for soft/hard/delayed trigger modes.
  private preChatStatus: "pending" | "shown" | "completed" = "pending";
  // True after the first server `done` for a user-initiated message —
  // used by `delayed` mode to show the form right after the first reply.
  private hasReceivedFirstDone: boolean = false;

  // Streaming message state
  private streamingMessageId: string | null = null;
  private streamingContent: string = "";
  private feedbackClickTimestamps: Map<string, number> = new Map();

  // Flag to track pending config updates during streaming
  private pendingRender: boolean = false;

  // User identity for personalization
  private _userIdentity: UserIdentity | null = null;

  get userIdentity(): UserIdentity | null {
    return this._userIdentity;
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  // Observed attributes for reactivity
  static get observedAttributes(): string[] {
    return [
      "widget-id",
      "theme",
      "position",
      "api-url",
      "config",
      "preview",
      "preview-state",
    ];
  }

  /**
   * Called when element is added to DOM.
   */
  async connectedCallback(): Promise<void> {
    this.widgetId = this.getAttribute("widget-id");
    this.apiUrl = this.getAttribute("api-url") || this.apiUrl;
    this.isPreviewMode = this.getAttribute("preview") === "true";
    this.previewStartsOpen = this.getAttribute("preview-state") !== "closed";

    // Check for inline config (preview mode)
    const inlineConfig = this.getAttribute("config");
    if (inlineConfig) {
      await this.initializeWithConfig(inlineConfig);
      return;
    }

    // Production mode requires widget-id
    if (!this.widgetId) {
      console.error("[LizaDocs] widget-id or config attribute is required");
      return;
    }

    // Fetch config and initialize
    await this.initialize();
  }

  /**
   * Called when element is removed from DOM.
   */
  disconnectedCallback(): void {
    this.destroy();
  }

  /**
   * Called when observed attributes change.
   */
  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case "widget-id":
        if (newValue && newValue !== this.widgetId) {
          this.widgetId = newValue;
          this.reinitialize();
        }
        break;
      case "theme":
        this.updateTheme(newValue as "light" | "dark");
        break;
      case "position":
        this.updatePosition(newValue as "bottom-right" | "bottom-left");
        break;
      case "api-url":
        this.apiUrl = newValue || "https://api.lizadocs.com/api";
        break;
      case "config":
        if (newValue) {
          this.updateConfig(newValue);
        }
        break;
      case "preview":
        this.isPreviewMode = newValue === "true";
        break;
      case "preview-state":
        this.previewStartsOpen = newValue !== "closed";
        if (this.isPreviewMode) {
          if (this.previewStartsOpen) this.open();
          else this.close();
        }
        break;
    }
  }

  // ========================================================================
  // Public API
  // ========================================================================

  /**
   * Open the chat window.
   */
  open(): void {
    if (this.state.isOpen) return;
    this.state.isOpen = true;
    this.ensureConnected();
    this.updateUI();
    this.dispatchEvent(new CustomEvent("widget:opened"));
    this.maybeShowPreChatForm("open");
  }

  /**
   * Close the chat window.
   */
  close(): void {
    if (!this.state.isOpen) return;
    this.state.isOpen = false;
    this.scheduleIdleDisconnect();
    this.updateUI();
    this.dispatchEvent(new CustomEvent("widget:closed"));
  }

  /**
   * Toggle the chat window.
   */
  toggle(): void {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Identify the current user.
   */
  identify(user: UserIdentity): void {
    this._userIdentity = user;
    // TODO: Send identify message via WebSocket if needed
  }

  /**
   * Destroy the widget and clean up resources.
   */
  destroy(): void {
    if (this.idleDisconnectTimer) {
      clearTimeout(this.idleDisconnectTimer);
      this.idleDisconnectTimer = null;
    }
    this.websocket?.destroy();
    this.websocket = null;
    this.contactFormElement?.remove();
    this.contactFormElement = null;
    this.contactFormLabels = undefined;
    this.preChatFormElement?.remove();
    this.preChatFormElement = null;
    this.shadow.replaceChildren();
  }

  // ========================================================================
  // Initialization
  // ========================================================================

  /**
   * Initialize the widget.
   */
  private async initialize(): Promise<void> {
    if (!this.widgetId) return;

    // Inject styles
    this.injectStyles();

    // Fetch config from API
    const apiConfig = await fetchWidgetConfig(this.widgetId, this.apiUrl);

    // Merge with attribute overrides
    const attributeOverrides: Partial<WidgetConfig> = {};

    const themeAttr = this.getAttribute("theme");
    if (themeAttr === "light" || themeAttr === "dark") {
      attributeOverrides.theme = themeAttr;
    }

    const positionAttr = this.getAttribute("position");
    if (positionAttr === "bottom-right") {
      attributeOverrides.anchor = "bottom_right";
    } else if (positionAttr === "bottom-left") {
      attributeOverrides.anchor = "bottom_left";
    }

    this.config = mergeConfig(apiConfig.config, attributeOverrides);
    this.preChatLabels = apiConfig.preChatLabels;

    // Add initial message
    if (this.config.initial_message && this.state.messages.length === 0) {
      this.state.messages.push({
        id: "initial",
        role: "assistant",
        content: this.config.initial_message,
      });
    }

    // Render UI
    this.render();

    // No WebSocket yet - connections open lazily on first open() so embeds
    // do not hold a socket per page view. Covers the case where isOpen was
    // restored before initialize finished.
    if (this.state.isOpen) {
      this.ensureConnected();
    }
  }

  /**
   * Reinitialize (when widget-id changes).
   */
  private async reinitialize(): Promise<void> {
    this.destroy();
    this.state = {
      isOpen: false,
      isConnected: false,
      isLoading: false,
      messages: [],
    };

    // Check for inline config first (preview mode with widgetId for WebSocket)
    const inlineConfig = this.getAttribute("config");
    if (inlineConfig) {
      await this.initializeWithConfig(inlineConfig);
      return;
    }

    await this.initialize();
  }

  /**
   * Initialize with inline config (preview mode).
   * Skips API fetch and WebSocket connection.
   */
  private async initializeWithConfig(configJson: string): Promise<void> {
    let config: WidgetConfig;
    try {
      config = JSON.parse(configJson);
    } catch (error) {
      console.error("[LizaDocs] Invalid config JSON:", error);
      return;
    }

    this.config = config;
    this.preChatLabels = preChatLabelsForLanguage(config.language);

    // Inject styles
    this.injectStyles();

    // Add initial message
    if (config.initial_message && this.state.messages.length === 0) {
      this.state.messages.push({
        id: "initial",
        role: "assistant",
        content: config.initial_message,
      });
    }

    // Preview-only: seed a sample conversation so the dashboard showcase can
    // demonstrate sources + feedback without a live WebSocket. Gated on
    // isPreviewMode so production never falls back to mock messages.
    if (this.isPreviewMode && Array.isArray(config.preview_messages)) {
      for (const seed of config.preview_messages) {
        this.state.messages.push({
          id: `preview_${this.state.messages.length}`,
          role: seed.role,
          content: seed.content,
          sources: seed.role === "assistant" ? seed.sources : undefined,
          feedback: seed.feedback ?? null,
          isStreaming: false,
        });
      }
    }

    // Preview mode starts open
    if (this.isPreviewMode) {
      this.state.isOpen = this.previewStartsOpen;
    }

    // Render UI
    this.render();

    // Preview starts open, so the live-preview WebSocket (when a widgetId is
    // provided) connects here; a closed preview connects lazily like production.
    if (this.state.isOpen) {
      this.ensureConnected();
    }
  }

  /**
   * Update config from attribute change (live preview updates).
   */
  private updateConfig(configJson: string): void {
    let config: WidgetConfig;
    try {
      config = JSON.parse(configJson);
    } catch (error) {
      console.error("[LizaDocs] Invalid config JSON:", error);
      return;
    }

    const oldTrigger = this.config?.pre_chat_trigger ?? "off";
    this.config = config;
    this.preChatLabels = preChatLabelsForLanguage(config.language);

    // Reset pre-chat status when trigger changes so live preview
    // reflects the new mode immediately (e.g. off → hard).
    const newTrigger = config.pre_chat_trigger ?? "off";
    if (oldTrigger !== newTrigger && this.preChatStatus === "completed") {
      this.preChatStatus = "pending";
    }

    // Update initial message if changed
    const initialMsg = this.state.messages.find((m) => m.id === "initial");
    if (config.initial_message) {
      if (initialMsg) {
        initialMsg.content = config.initial_message;
      } else if (this.state.messages.length === 0) {
        this.state.messages.push({
          id: "initial",
          role: "assistant",
          content: config.initial_message,
        });
      }
    }

    // If streaming is active, defer full render to avoid DOM corruption
    // Just apply styles for now, render() will be called in handleDone()
    if (this.streamingMessageId) {
      this.pendingRender = true;
      this.applyCustomStyles();
      return;
    }

    // Re-render to apply new config
    this.render();
  }

  /**
   * Inject CSS styles into shadow DOM.
   */
  private injectStyles(): void {
    const style = document.createElement("style");
    style.textContent = `${baseStyles}\n${themeStyles}\n${markdownStyles}\n${presetStyles}\n${buildKatexStyles(widgetAssetBaseUrl)}`;
    this.shadow.appendChild(style);
  }

  // ========================================================================
  // WebSocket
  // ========================================================================

  /**
   * Make sure a connection exists or is being established.
   * Called on open() and before sending - the widget connects lazily, so
   * this is the single entry point that revives a dropped or never-started
   * connection. Cancels a pending idle teardown from a recent close().
   */
  private ensureConnected(): void {
    if (this.idleDisconnectTimer) {
      clearTimeout(this.idleDisconnectTimer);
      this.idleDisconnectTimer = null;
    }

    if (!this.widgetId) return;

    if (!this.websocket) {
      this.connectWebSocket();
      return;
    }

    if (!this.websocket.isConnected) {
      this.websocket.connect();
    }
  }

  /**
   * Drop the connection after the widget has been closed for a while.
   * The grace period keeps quick toggles cheap; the isLoading check defers
   * teardown while a reply is still streaming in a closed window.
   */
  private scheduleIdleDisconnect(): void {
    if (this.idleDisconnectTimer) {
      clearTimeout(this.idleDisconnectTimer);
    }

    this.idleDisconnectTimer = setTimeout(() => {
      this.idleDisconnectTimer = null;
      if (this.state.isLoading) {
        this.scheduleIdleDisconnect();
        return;
      }
      this.websocket?.disconnect();
    }, IDLE_DISCONNECT_MS);
  }

  /**
   * Connect to WebSocket server.
   */
  private connectWebSocket(): void {
    if (!this.widgetId || this.websocket) return;

    this.websocket = new WidgetWebSocket(
      this.widgetId,
      {
        onChunk: (content) => this.handleChunk(content),
        onDone: (messageId, sources) => this.handleDone(messageId, sources),
        onError: (message) => this.handleError(message),
        onHistory: (messages) => this.handleHistory(messages),
        onConnectionChange: (connected) =>
          this.handleConnectionChange(connected),
        onRequestContactForm: (labels) => this.handleRequestContactForm(labels),
        onContactFormReceived: () => this.handleContactFormReceived(),
        onPreChatReceived: () => this.handlePreChatReceived(),
      },
      this.apiUrl,
      this.isPreviewMode,
    );

    this.websocket.connect();
  }

  /**
   * Handle streaming chunk.
   */
  private handleChunk(content: string): void {
    this.streamingContent += content;

    if (!this.streamingMessageId) {
      // Create new streaming message
      this.streamingMessageId = `msg_${Date.now()}`;
      const message: Message = {
        id: this.streamingMessageId,
        role: "assistant",
        content: this.streamingContent,
        isStreaming: true,
      };
      this.state.messages.push(message);

      // Hide loading, add message
      if (this.chatWindow) {
        hideTypingIndicator(this.chatWindow);
        addChatMessage(
          this.chatWindow,
          message,
          this.config?.avatar_url,
          undefined,
          (messageId, rating) => this.handleFeedback(messageId, rating),
        );
      }
    } else {
      // Update existing message
      const message = this.state.messages.find(
        (m) => m.id === this.streamingMessageId,
      );
      if (message) {
        message.content = this.streamingContent;
        if (this.chatWindow) {
          updateChatMessage(
            this.chatWindow,
            this.streamingMessageId,
            this.streamingContent,
            true,
          );
        }
      }
    }
  }

  /**
   * Handle stream done.
   */
  private handleDone(messageId: string, sources?: MessageSource[]): void {
    // Finalize streaming message
    if (this.streamingMessageId) {
      const message = this.state.messages.find(
        (m) => m.id === this.streamingMessageId,
      );
      if (message) {
        message.id = messageId;
        message.isStreaming = false;
        // Backend sends sources only when the widget has show_sources enabled.
        if (sources?.length) {
          message.sources = sources;
        }

        if (this.chatWindow) {
          // Update messagesMap key to use real server ID
          const messageEl = this.chatWindow.messageList.messagesMap.get(
            this.streamingMessageId,
          );
          if (messageEl) {
            this.chatWindow.messageList.messagesMap.delete(
              this.streamingMessageId,
            );
            this.chatWindow.messageList.messagesMap.set(messageId, messageEl);
            messageEl.dataset.messageId = messageId;
          }

          // Wait for async highlighting to complete, then check overflow
          updateChatMessage(
            this.chatWindow,
            messageId,
            message.content,
            false,
            undefined,
            (msgId, rating) => this.handleFeedback(msgId, rating),
            message.sources,
          )
            .then(() => {
              this.checkForOverflow();
            })
            .catch((error) => {
              console.error(
                "[LizaDocs] Failed to finalize streamed message rendering:",
                error,
              );
              this.checkForOverflow();
            });
        }
      }
    }

    // Reset streaming state
    this.streamingMessageId = null;
    this.streamingContent = "";
    this.state.isLoading = false;

    // Re-enable input
    if (this.chatWindow) {
      setInputEnabled(this.chatWindow, true);
    }

    // If config was updated during streaming, do a full render now
    if (this.pendingRender) {
      this.pendingRender = false;
      this.render();
    }

    // Dispatch event
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (msg) {
      this.dispatchEvent(
        new CustomEvent("message:received", {
          detail: { message_id: messageId, content: msg.content },
        }),
      );
    }

    // Delayed pre-chat trigger fires after the first AI response — by then
    // the visitor has gotten one answer and is more receptive to the form.
    if (!this.hasReceivedFirstDone) {
      this.hasReceivedFirstDone = true;
      if (this.config?.pre_chat_trigger === "delayed") {
        this.maybeShowPreChatForm("delayed-trigger");
      }
    }
  }

  /**
   * Handle error.
   */
  private handleError(message: string): void {
    console.error("[LizaDocs] Error:", message);

    // Hide loading
    if (this.chatWindow) {
      hideTypingIndicator(this.chatWindow);
      setInputEnabled(this.chatWindow, true);
    }

    // Reset streaming state
    this.streamingMessageId = null;
    this.streamingContent = "";
    this.state.isLoading = false;

    // Show error message
    const errorMessage = this.config?.error_message || message;
    this.state.messages.push({
      id: `error_${Date.now()}`,
      role: "assistant",
      content: errorMessage,
    });

    if (this.chatWindow) {
      addChatMessage(
        this.chatWindow,
        this.state.messages[this.state.messages.length - 1],
        this.config?.avatar_url,
        undefined,
        (messageId, rating) => this.handleFeedback(messageId, rating),
      );
    }

    this.dispatchEvent(new CustomEvent("error", { detail: { message } }));
  }

  /**
   * Handle conversation history restore.
   */
  private handleHistory(_messages: HistoryMessage[]): void {
    // No-op: Don't restore history. Fresh start each session.
  }

  /**
   * Handle connection state change.
   */
  private handleConnectionChange(connected: boolean): void {
    this.state.isConnected = connected;

    if (connected && this.state.messages.length === 0) {
      this.dispatchEvent(
        new CustomEvent("conversation:started", {
          detail: { conversation_id: "tbd" },
        }),
      );
    }
  }

  /**
   * Handle backend request to show a contact form (frustration detected).
   */
  private handleRequestContactForm(labels?: ContactFormLabels): void {
    if (this.contactFormElement || !this.chatWindow) return;

    this.contactFormLabels = labels;

    const { container, nameInput, emailInput } = createContactForm(
      (name: string, email: string) => {
        this.websocket?.sendContactInfo(name, email);
      },
      labels,
    );

    // Pre-fill from identify() if available
    if (this._userIdentity?.name) {
      nameInput.value = this._userIdentity.name;
    }
    if (this._userIdentity?.email) {
      emailInput.value = this._userIdentity.email;
    }

    this.contactFormElement = container;

    // Insert between message list and input area
    const inputArea = this.chatWindow.inputArea.container;
    inputArea.parentNode?.insertBefore(container, inputArea);
  }

  /**
   * Mount the pre-chat form when the trigger mode and lifecycle allow it.
   * `reason` distinguishes the two entry points so each mode only mounts in
   * response to its own trigger (`open` for soft/hard, `delayed-trigger` for
   * delayed).
   */
  private maybeShowPreChatForm(reason: "open" | "delayed-trigger"): void {
    if (!this.config || !this.chatWindow) return;
    if (this.preChatStatus !== "pending") return;
    if (this.preChatFormElement) return;

    if (this.isPreChatCompletedThisSession()) {
      this.preChatStatus = "completed";
      return;
    }

    const trigger = this.config.pre_chat_trigger ?? "off";
    if (trigger === "off") {
      this.preChatStatus = "completed";
      return;
    }

    if (trigger === "delayed" && reason !== "delayed-trigger") return;
    if ((trigger === "soft" || trigger === "hard") && reason !== "open") {
      return;
    }

    const allowSkip = trigger === "soft" || trigger === "delayed";

    const initialEmail =
      typeof this._userIdentity?.email === "string"
        ? this._userIdentity.email
        : undefined;
    const initialName =
      typeof this._userIdentity?.name === "string"
        ? this._userIdentity.name
        : undefined;

    const { container } = createPreChatForm({
      allowSkip,
      initialName,
      initialEmail,
      labels: this.preChatLabels ?? undefined,
      onSubmit: (name, email, phone) => {
        this.websocket?.sendPreChatInfo(name, email, phone);
      },
      onSkip: () => {
        this.markPreChatCompleted();
        this.removePreChatForm();
      },
    });

    this.preChatFormElement = container;
    this.preChatStatus = "shown";

    // Sit above the input area, like the frustration form does.
    const inputArea = this.chatWindow.inputArea.container;
    inputArea.parentNode?.insertBefore(container, inputArea);

    // Hard mode gates the chat input until the visitor submits the form.
    if (trigger === "hard") {
      setInputEnabled(this.chatWindow, false);
    }
  }

  /**
   * Remove the pre-chat form from the DOM. Idempotent.
   */
  private removePreChatForm(): void {
    this.preChatFormElement?.remove();
    this.preChatFormElement = null;
  }

  /**
   * Mark pre-chat as completed in-memory and persist to sessionStorage so
   * a re-mount in the same browser session does not re-show the form.
   */
  private markPreChatCompleted(): void {
    this.preChatStatus = "completed";
    try {
      sessionStorage.setItem(PRE_CHAT_COMPLETED_STORAGE_KEY, "1");
    } catch {
      // sessionStorage may throw under privacy modes / sandboxed iframes —
      // the in-memory status field is still authoritative for this instance.
    }
  }

  /**
   * Check the sessionStorage flag set by markPreChatCompleted in a prior
   * widget mount within the same browser session.
   */
  private isPreChatCompletedThisSession(): boolean {
    try {
      return sessionStorage.getItem(PRE_CHAT_COMPLETED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  /**
   * Handle backend acknowledgment of the pre-chat info submission.
   */
  private handlePreChatReceived(): void {
    this.markPreChatCompleted();
    this.removePreChatForm();
    // Re-enable the chat input — was disabled in `hard` mode.
    if (this.chatWindow) {
      setInputEnabled(this.chatWindow, true);
    }
  }

  /**
   * Handle backend acknowledgment that contact info was received.
   */
  private handleContactFormReceived(): void {
    if (!this.contactFormElement) return;

    const thanks = document.createElement("div");
    thanks.className = "contact-form-thanks";
    thanks.textContent =
      this.contactFormLabels?.thank_you ||
      "Thank you! Our team will follow up with you soon.";

    this.contactFormElement.replaceWith(thanks);
    this.contactFormElement = null;

    // Auto-remove after 5 seconds
    setTimeout(() => {
      thanks.remove();
    }, 5000);
  }

  // ========================================================================
  // Message Sending
  // ========================================================================

  /**
   * Send a message.
   */
  private sendMessage(content: string): void {
    if (!content.trim()) return;

    // Revives the connection if the idle teardown or a proxy dropped it
    // between opening the window and hitting send.
    this.ensureConnected();
    if (!this.websocket) return;

    // Add user message
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: "user",
      content: content.trim(),
    };
    this.state.messages.push(userMessage);

    if (this.chatWindow) {
      addChatMessage(this.chatWindow, userMessage);
      showTypingIndicator(this.chatWindow);
      setInputEnabled(this.chatWindow, false);
    }

    // Send via WebSocket
    this.websocket.send(content.trim());
    this.state.isLoading = true;

    this.dispatchEvent(
      new CustomEvent("message:sent", { detail: { content } }),
    );
  }

  // ========================================================================
  // Feedback
  // ========================================================================

  /**
   * Handle feedback button click.
   */
  private async handleFeedback(
    messageId: string,
    rating: "up" | "down",
  ): Promise<void> {
    const message = this.state.messages.find((m) => m.id === messageId);
    if (!message) return;

    const now = Date.now();
    const lastClickAt = this.feedbackClickTimestamps.get(messageId) ?? 0;
    if (now - lastClickAt < 500) {
      return;
    }
    this.feedbackClickTimestamps.set(messageId, now);

    // Toggle: clicking same rating removes it, different rating changes it
    const newRating = message.feedback === rating ? null : rating;

    // Optimistic UI update
    const previousRating = message.feedback ?? null;
    message.feedback = newRating;
    this.updateFeedbackUI(messageId, newRating);

    try {
      if (newRating) {
        await submitFeedback(this.apiUrl, this.widgetId, messageId, newRating);
      } else {
        await removeFeedback(this.apiUrl, this.widgetId, messageId);
      }
    } catch (error) {
      // Revert on error
      console.error("[LizaDocs] Failed to submit feedback:", error);
      message.feedback = previousRating;
      this.updateFeedbackUI(messageId, previousRating);
    }
  }

  /**
   * Update feedback button state in the UI.
   */
  private updateFeedbackUI(
    messageId: string,
    feedback: "up" | "down" | null,
  ): void {
    if (!this.chatWindow) return;

    const messageEl = this.chatWindow.messageList.messagesMap.get(messageId);
    if (!messageEl) return;

    const upButton = messageEl.querySelector(".feedback-button.up");
    const downButton = messageEl.querySelector(".feedback-button.down");

    upButton?.classList.toggle("active", feedback === "up");
    downButton?.classList.toggle("active", feedback === "down");
  }

  // ========================================================================
  // Rendering
  // ========================================================================

  /**
   * Initial render.
   */
  private render(): void {
    if (!this.config) return;

    // Clear existing content (except styles)
    const style = this.shadow.querySelector("style");
    this.shadow.innerHTML = "";
    if (style) this.shadow.appendChild(style);

    // Main container
    this.container = document.createElement("div");
    this.container.className = `widget-container ${this.config.anchor.replace("_", "-")}`;
    if (this.state.isOpen) {
      this.container.classList.add("chat-open");
    }

    // Apply theme. Toggle, not add: a live config swap (preview) must be able
    // to leave dark mode, not just enter it.
    this.classList.toggle("dark", this.config.theme === "dark");

    // Preset: a cohesive look bundle layered on Classic via a host class,
    // mirroring the .dark mechanism above. "classic" / absent / unknown means
    // the default Classic look, so no class is added for those. Stale preset
    // classes are cleared first so a live config swap (preview) can change look.
    // Clear every preset-* class generically so new skins never need this
    // list updated (stale classes would survive a live config swap).
    for (const className of [...this.classList]) {
      if (className.startsWith("preset-")) this.classList.remove(className);
    }
    if (this.config.preset && this.config.preset !== "classic") {
      this.classList.add(`preset-${this.config.preset}`);
      // The preset's signature message layout (rails / cards) is gated behind
      // `preset-modern`. Absent or "signature" message_style keeps it; "bubble"
      // or "flat" drops the gate so classic bubbles / the flat rule show through
      // with the preset's palette + chrome intact.
      const messageStyle = this.config.message_style ?? "signature";
      if (messageStyle === "signature") {
        this.classList.add("preset-modern");
      }
    }

    // Apply custom CSS variables
    this.applyCustomStyles();

    // Toggle button
    this.toggleButton = createFloatingButton({
      isOpen: this.state.isOpen,
      onClick: () => this.toggle(),
      launcher: this.launcherOptions(),
    });
    this.container.appendChild(this.toggleButton);

    // Chat window
    this.chatWindow = createChatWindow({
      config: this.config,
      messages: this.state.messages,
      isOpen: this.state.isOpen,
      onClose: () => this.close(),
      onSend: (msg) => this.sendMessage(msg),
      onCopyCode: (code) =>
        console.log("[LizaDocs] Code copied:", code.substring(0, 50)),
      onFeedback: (messageId, rating) => this.handleFeedback(messageId, rating),
      greetingTitle: this.config.greeting_title,
      greetingSubtitle: this.config.greeting_subtitle,
    });
    this.container.appendChild(this.chatWindow.container);

    this.shadow.appendChild(this.container);

    // The previous chatWindow DOM is gone after re-render, so any mounted
    // pre-chat form was wiped with it. Reset the reference + revert "shown"
    // back to "pending" so maybeShowPreChatForm can re-mount when applicable.
    this.preChatFormElement = null;
    if (this.preChatStatus === "shown") {
      this.preChatStatus = "pending";
    }
    if (this.state.isOpen) {
      this.maybeShowPreChatForm("open");
    }
  }

  /**
   * Update UI after state change.
   */
  private updateUI(): void {
    if (!this.toggleButton || !this.chatWindow || !this.container) return;

    updateFloatingButton(this.toggleButton, this.state.isOpen, this.launcherOptions());
    setChatWindowVisibility(this.chatWindow, this.state.isOpen);
    this.container.classList.toggle("chat-open", this.state.isOpen);
  }

  /**
   * Check if content overflows horizontally and expand chat window if needed.
   */
  private checkForOverflow(): void {
    if (!this.chatWindow) return;

    const container = this.chatWindow.messageList.container;
    if (container.scrollWidth > container.clientWidth + 1) {
      this.chatWindow.container.classList.add("expanded");
    }
  }

  /**
   * Launcher knobs handled by FloatingButton itself (shape/icon/label).
   * Colour and size travel separately as CSS vars in applyCustomStyles.
   */
  private launcherOptions(): LauncherOptions {
    if (!this.config) return {};
    return {
      shape: this.config.launcher_shape,
      icon: this.config.launcher_icon,
      iconUrl: this.config.launcher_icon_url,
      label: this.config.launcher_label,
    };
  }

  /**
   * Apply custom styles from config.
   */
  private applyCustomStyles(): void {
    if (!this.config) return;

    const vars: Record<string, string> = {};

    // Accent derivation runs FIRST so every explicit knob below overrides it.
    if (this.config.accent_color) {
      Object.assign(
        vars,
        deriveAccentPalette(this.config.accent_color, this.config.theme === "dark"),
      );
    }

    if (this.config.primary_color)
      vars["--primary-color"] = this.config.primary_color;
    if (this.config.background_color)
      vars["--widget-bg"] = this.config.background_color;
    if (this.config.text_color) vars["--text-color"] = this.config.text_color;
    if (this.config.header_color)
      vars["--header-text-color"] = this.config.header_color;
    if (this.config.border_radius)
      vars["--window-radius"] = this.config.border_radius;
    if (this.config.font_family)
      vars["--font-family"] = this.config.font_family;

    // Message bubble colors
    if (this.config.user_message_bg)
      vars["--user-msg-bg"] = this.config.user_message_bg;
    if (this.config.user_message_color)
      vars["--user-msg-color"] = this.config.user_message_color;
    if (this.config.assistant_message_bg)
      vars["--bot-msg-bg"] = this.config.assistant_message_bg;
    if (this.config.assistant_message_color)
      vars["--bot-msg-color"] = this.config.assistant_message_color;

    // Launcher: shape/icon/label are applied in FloatingButton; colour and
    // size land here as CSS vars whose fallbacks reproduce today's look.
    if (this.config.launcher_color)
      vars["--launcher-bg"] = this.config.launcher_color;
    if (this.config.launcher_icon_color)
      vars["--launcher-icon-color"] = this.config.launcher_icon_color;
    if (this.config.launcher_size) {
      const launcherSizes: Record<string, string> = {
        small: "48px",
        medium: "56px",
        large: "64px",
      };
      vars["--launcher-size"] = launcherSizes[this.config.launcher_size];
    }

    // Surfaces / bubbles / typography / motion / sizing
    if (this.config.header_background_color)
      vars["--header-bg"] = this.config.header_background_color;
    if (this.config.border_color)
      vars["--border-color"] = this.config.border_color;
    if (this.config.input_bg) {
      vars["--input-bg"] = this.config.input_bg;
      vars["--input-field-bg"] = this.config.input_bg;
    }
    if (this.config.bubble_radius)
      vars["--bubble-radius"] = this.config.bubble_radius;
    if (this.config.send_button_color)
      vars["--send-bg"] = this.config.send_button_color;
    if (this.config.send_icon_color)
      vars["--send-icon-color"] = this.config.send_icon_color;
    if (this.config.source_pill_bg)
      vars["--source-pill-bg"] = this.config.source_pill_bg;
    if (this.config.source_pill_color)
      vars["--source-pill-color"] = this.config.source_pill_color;
    if (this.config.font_scale) {
      const fontScales: Record<string, string> = {
        compact: "13px",
        normal: "14px",
        comfortable: "15px",
      };
      vars["--font-size-base"] = fontScales[this.config.font_scale];
    }
    if (this.config.motion === "reduced") {
      vars["--transition-fast"] = "0s";
      vars["--transition-normal"] = "0s";
    }
    if (this.config.window_width) {
      const chatWidths: Record<string, string> = {
        standard: "min(45dvw, 400px)",
        wide: "min(45dvw, 480px)",
      };
      vars["--chat-width"] = chatWidths[this.config.window_width];
    }

    vars["--offset-x"] = `${this.config.offset_x}px`;
    vars["--offset-y"] = `${this.config.offset_y}px`;

    // Apply to host
    const styleString = Object.entries(vars)
      .map(([k, v]) => `${k}: ${v}`)
      .join("; ");
    this.style.cssText = styleString;

    this.classList.toggle("motion-reduced", this.config.motion === "reduced");
    this.classList.toggle("no-message-rule", this.config.message_rule === false);

    // Backend is the authoritative gate (no sources sent when disabled); this
    // class only hides preview-seeded strips so the dashboard toggle reacts live.
    this.classList.toggle("sources-hidden", !this.config.show_sources);

    if (this.chatWindow)
      this.chatWindow.container.classList.toggle(
        "message-style-flat",
        this.config.message_style === "flat",
      );

    this.applyLinkColor();
  }

  /**
   * Links keep the accent hue, adjusted until readable against the surface
   * the message text actually sits on. Computed from the host's resolved
   * custom properties because the effective colours come from theme and
   * preset CSS as much as from the inline knobs above - the cascade is the
   * only place they all meet.
   */
  private applyLinkColor(): void {
    if (!this.config) return;

    const resolved = getComputedStyle(this);
    const accent = resolved.getPropertyValue("--primary-color");
    // Flat mode has transparent bubbles, so text sits on the widget surface.
    // Presets may also set --bot-msg-bg: transparent; that parses to null and
    // falls through to the widget surface too.
    const linkOnBubble =
      this.config.message_style === "flat"
        ? null
        : readableLinkColor(accent, resolved.getPropertyValue("--bot-msg-bg"));
    const linkColor =
      linkOnBubble ??
      readableLinkColor(accent, resolved.getPropertyValue("--widget-bg"));

    if (linkColor) this.style.setProperty("--link-color", linkColor);
  }

  /**
   * Update theme.
   */
  private updateTheme(theme: "light" | "dark" | null): void {
    if (theme === "dark") {
      this.classList.add("dark");
    } else {
      this.classList.remove("dark");
    }

    if (this.config) {
      this.config.theme = theme || "light";
      // The theme swaps every surface var, so accent derivation and the
      // computed link colour are both stale until styles re-apply.
      this.applyCustomStyles();
    }
  }

  /**
   * Update position.
   */
  private updatePosition(
    position: "bottom-right" | "bottom-left" | null,
  ): void {
    if (!this.container || !this.config || !this.chatWindow) return;

    const anchor = position === "bottom-left" ? "bottom_left" : "bottom_right";
    this.config.anchor = anchor;

    this.container.classList.remove("bottom-right", "bottom-left");
    this.container.classList.add(position || "bottom-right");

    this.chatWindow.container.classList.remove("bottom-right", "bottom-left");
    this.chatWindow.container.classList.add(position || "bottom-right");
  }
}
